"use client";

import { delay } from "@/helpers/delay";
import { renewTopupPackage } from "@/services/purchase/purchaseApis";
import { RenewTopupPackageResult } from "@/types/purchase/TopupPackages";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";

interface UseRenewTopupPackageOptions {
  onRenewed?: (data: RenewTopupPackageResult) => void;
  backHref?: string;
}

export function useRenewTopupPackage({
  onRenewed,
  backHref,
}: UseRenewTopupPackageOptions = {}) {
  const locale = useLocale();
  const t = useTranslations("CheckoutPage.summary");
  const tRenew = useTranslations("RenewSimPage.text");
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async ({
      iccid,
      packageId,
    }: {
      iccid: string;
      packageId: string;
    }) => {
      const redirect_url =
        typeof window !== "undefined"
          ? `${window.location.origin}/sim-buy-thank-you/`
          : "";
      const res = await renewTopupPackage({
        iccid,
        packageId,
        redirect_url,
        locale,
      });
      if (!res.status) {
        throw new Error(res.message);
      }
      return res.data;
    },
    onSuccess: async (data) => {
      if (data.verified && data.open_checkout && data.stripe_checkout_url) {
        Cookies.set("isPurchase", "true");
        Cookies.set("isPaymentFromStripe", "true");
        if (backHref) {
          Cookies.set("renewBackHref", backHref);
        }
        toast.success(t("stripeRedirect"));
        await delay(1000);
        window.location.href = data.stripe_checkout_url;
        return;
      }
      if (data.message) {
        toast.success(data.message);
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return (
              typeof key === "string" &&
              (key.startsWith("data-usage-") ||
                key.startsWith("data-voice-usage-"))
            );
          },
        });
        onRenewed?.(data);
        return;
      }
      toast.error(tRenew("canNotRenew"));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return { renewTopup: mutate, isRenewingTopup: isPending };
}
